/**
 * Centralized plan configuration for Penzu checkout
 *
 * Tier names used in this app and their API membership_package_name mappings:
 *   - pro: standard
 *   - promonthly: standardmonthly
 *   - proplus: proplus
 *   - proplusmonthly: proplusmonthly
 *   - propluslifetime: ProPlusLifetime
 */

// ============ Types ============

/** Tier identifiers used in URLs and internally */
export type PlanTier = 'pro' | 'promonthly' | 'proplus' | 'proplusmonthly' | 'propluslifetime';

/** Billing period - yearly tiers support multi-year (1, 2, 3, or 5), monthly is always 1 */
export type BillingPeriod = 'monthly' | 'yearly' | 'lifetime';

export interface PlanConfig {
  /** Tier identifier (used in URLs) */
  tier: PlanTier;
  /** API membership_package_name value */
  membershipPackageName: string;
  /** Display name for UI */
  displayName: string;
  /** Short name for buttons */
  shortName: string;
  /** Billing period type */
  billingPeriod: BillingPeriod;
  /** Base price (yearly or monthly rate) */
  basePrice: number;
  /** Price label suffix ("/ year", "/ month", or empty) */
  periodLabel: string;
  /** Marketing description */
  description: string;
  /** Feature list */
  features: string[];
  /** Feature list heading */
  featuresHeading: string;
  /** Show "Most Popular" badge */
  popular?: boolean;
  /** Exact annual offer totals, in cents. */
  annualPricesCents?: Readonly<Record<AnnualTerm, number>>;
  /** Whether this tier supports multi-year purchase (1, 2, 3, or 5 years) */
  supportsMultiYear: boolean;
}

// ============ Tier to API Mapping ============

export const TIER_TO_MEMBERSHIP_PACKAGE: Record<PlanTier, string> = {
  pro: 'standard',
  promonthly: 'standardmonthly',
  proplus: 'proplus',
  proplusmonthly: 'proplusmonthly',
  propluslifetime: 'ProPlusLifetime',
};

// ============ Plan Definitions ============

/** Monetary defaults are v1; quoted mode replaces prices while retaining plan identity and display metadata. */
export const PLANS: Record<PlanTier, PlanConfig> = {
  pro: {
    tier: 'pro',
    membershipPackageName: 'standard',
    displayName: 'Penzu Pro',
    shortName: 'Pro',
    billingPeriod: 'yearly',
    basePrice: 19.99,
    periodLabel: '/ year',
    description: 'Make it yours with customization, plus pro-active features to keep you writing.',
    featuresHeading: 'Everything in Basic, plus:',
    features: [
      'Encryption locking',
      'Custom journal covers, fonts & colors',
      'Entry tagging',
      'PDF export',
      'Writing prompts',
      'Write via email (Penzu Post)',
    ],
    annualPricesCents: { 1: 1999, 2: 2999, 3: 3999, 5: 5999 },
    supportsMultiYear: true,
  },

  promonthly: {
    tier: 'promonthly',
    membershipPackageName: 'standardmonthly',
    displayName: 'Penzu Pro',
    shortName: 'Pro',
    billingPeriod: 'monthly',
    basePrice: 4.99,
    periodLabel: '/ month',
    description: 'Make it yours with customization, plus pro-active features to keep you writing.',
    featuresHeading: 'Everything in Basic, plus:',
    features: [
      'Encryption locking',
      'Custom journal covers, fonts & colors',
      'Entry tagging',
      'PDF export',
      'Writing prompts',
      'Write via email (Penzu Post)',
    ],
    supportsMultiYear: false,
  },

  proplus: {
    tier: 'proplus',
    membershipPackageName: 'proplus',
    displayName: 'Penzu Pro+',
    shortName: 'Pro+',
    billingPeriod: 'yearly',
    basePrice: 49.99,
    periodLabel: '/ year',
    description: 'The most powerful suite of tools for journaling.',
    featuresHeading: 'Everything in Pro, plus:',
    features: [
      'Premium handwritten fonts',
      'Fullscreen zen-mode',
      'Versions & entry recovery',
      'Premium support',
      'Military grade encryption locking',
    ],
    popular: true,
    annualPricesCents: { 1: 4999, 2: 7499, 3: 9999, 5: 14999 },
    supportsMultiYear: true,
  },

  proplusmonthly: {
    tier: 'proplusmonthly',
    membershipPackageName: 'proplusmonthly',
    displayName: 'Penzu Pro+',
    shortName: 'Pro+',
    billingPeriod: 'monthly',
    basePrice: 9.99,
    periodLabel: '/ month',
    description: 'The most powerful suite of tools for journaling.',
    featuresHeading: 'Everything in Pro, plus:',
    features: [
      'Premium handwritten fonts',
      'Fullscreen zen-mode',
      'Versions & entry recovery',
      'Premium support',
      'Military grade encryption locking',
    ],
    popular: true,
    supportsMultiYear: false,
  },

  propluslifetime: {
    tier: 'propluslifetime',
    membershipPackageName: 'ProPlusLifetime',
    displayName: 'Penzu Pro+ Lifetime',
    shortName: 'Pro+ Lifetime',
    billingPeriod: 'lifetime',
    basePrice: 199.99,
    periodLabel: '',
    description: 'The biggest upgrade you can make to your life, for life.',
    featuresHeading: 'Everything in Pro+, including:',
    features: ['One-time payment', 'No need to renew', '24hr support'],
    supportsMultiYear: false,
  },
};

// ============ Helper Functions ============

/**
 * Get a plan by its tier
 */
export function getPlanByTier(tier: PlanTier): PlanConfig {
  return PLANS[tier];
}

/**
 * Check if a string is a valid tier
 */
export function isValidTier(tier: string): tier is PlanTier {
  return tier in PLANS;
}

/**
 * Parse tier from URL parameter (case-insensitive)
 */
export function parseTierParam(param: string | null): PlanTier | undefined {
  if (!param) return undefined;
  const normalized = param.toLowerCase();
  if (isValidTier(normalized)) {
    return normalized;
  }
  return undefined;
}

/** Annual products supported by the current billing API. */
export const ANNUAL_TERMS = [1, 2, 3, 5] as const;
export type AnnualTerm = (typeof ANNUAL_TERMS)[number];
export const INVALID_SUBSCRIPTION_TERM_MESSAGE =
  'Please choose a supported subscription length before paying.';

export function isAnnualTerm(years: number): years is AnnualTerm {
  return ANNUAL_TERMS.some((term) => term === years);
}

export function requireAnnualTerm(years: number): AnnualTerm {
  if (!isAnnualTerm(years)) throw new Error(INVALID_SUBSCRIPTION_TERM_MESSAGE);
  return years;
}

/** Payment inputs must be explicit; never default or round an invalid purchase. */
export function isValidSubscriptionLength(tier: PlanTier, years: number): years is AnnualTerm {
  return PLANS[tier].billingPeriod === 'yearly' ? isAnnualTerm(years) : years === 1;
}

/** Parse initial navigation only; unsupported URL terms visibly start at one year. */
export function parsePeriodParam(param: string | null): AnnualTerm {
  return ANNUAL_TERMS.find((term) => String(term) === param) ?? 1;
}

/** Keep initial selections and plan switches compatible with their billing period. */
export function initialSubscriptionLength(tier: PlanTier | null, years: number): AnnualTerm {
  if (tier && PLANS[tier].billingPeriod !== 'yearly') return 1;
  return isAnnualTerm(years) ? years : 1;
}

/** Exact legacy totals; runtime offers supply a plan's annualPricesCents. */
export function calculatePlanTotalPrice(plan: PlanConfig, years: number = 1): number {
  if (!plan.supportsMultiYear) return plan.basePrice;
  const term = requireAnnualTerm(years);
  const cents = plan.annualPricesCents?.[term];
  if (cents === undefined) throw new Error('Pricing unavailable');
  return cents / 100;
}

export function calculateTotalPrice(tier: PlanTier, years: number = 1): number {
  return calculatePlanTotalPrice(PLANS[tier], years);
}

/**
 * Format price for display
 */
export function formatPrice(price: number): string {
  return `$${price.toFixed(2)}`;
}

export function shortFormatPrice(price: number): string {
  return `$${price.toFixed(0)}`;
}

/**
 * Get yearly tiers for plan selector (when "Pay Yearly" is selected)
 */
export function getYearlyTiers(): PlanTier[] {
  return ['pro', 'proplus', 'propluslifetime'];
}

/**
 * Get monthly tiers for plan selector (when "Pay Monthly" is selected)
 */
export function getMonthlyTiers(): PlanTier[] {
  return ['promonthly', 'proplusmonthly', 'propluslifetime'];
}

/**
 * Get the corresponding yearly tier for a monthly tier (for toggle switching)
 */
export function getYearlyEquivalent(tier: PlanTier): PlanTier {
  switch (tier) {
    case 'promonthly':
      return 'pro';
    case 'proplusmonthly':
      return 'proplus';
    default:
      return tier;
  }
}

/**
 * Get the corresponding monthly tier for a yearly tier (for toggle switching)
 */
export function getMonthlyEquivalent(tier: PlanTier): PlanTier {
  switch (tier) {
    case 'pro':
      return 'promonthly';
    case 'proplus':
      return 'proplusmonthly';
    default:
      return tier;
  }
}
